import { useCallback, useEffect, useRef, useState } from 'react';
import type { LeadResource, SiteContext } from '@icetus/contracts';
import { MAX_RESOURCE_BYTES, formatFileSize, isResourceMime } from '@icetus/contracts';
import { Badge, Button, Card, visuallyHidden } from '@icetus/ui';
import {
  ApiError,
  createResource,
  deleteResource,
  fetchResources,
  fetchSites,
  presignResource,
  putToStorage,
  updateResource,
} from '../api';
import { useConfirm } from '../components/confirm';
import type { LoadPhase } from '../lib/load-phase';
import { isActive, overallPercent, rowLabel, rowPercent, type UploadRow } from '../lib/upload-progress';

/**
 * 资料库：客户上传的 PDF / 压缩包 / Office 文档。
 *
 * 与媒体库分成两页而不是混在一起：
 *   - 媒体库的界面是图片网格，缩略图是它的主体；PDF 放进去只能显示成一个
 *     灰色方块，而"看不出这是什么"的列表没有人会用；
 *   - 图片的产物是"可被引用的地址"，资料的产物是"一份能下载的文件 + 下载次数"。
 *     两者的生命周期与操作完全不同。
 *
 * 上传仍然是直传：先签票、浏览器 PUT 到对象存储、再回来登记。
 * 服务端在登记时用 HeadObject 核对真实大小与类型 —— 申报值只是申请。
 */

const ACCEPT = '.pdf,.zip,.docx,.xlsx,.pptx';

/**
 * 扩展名兜底。
 *
 * 浏览器对 zip / docx 的 `file.type` 经常给空串（尤其 Windows + 中文系统），
 * 而我们的白名单是按 MIME 判的。直接拿空 MIME 去签票会被服务端拒成
 * "不支持的文件类型"，客户看到的是"我选了个 PDF 说类型不对"。
 */
const EXT_MIME: Record<string, string> = {
  '.pdf': 'application/pdf',
  '.zip': 'application/zip',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
};

function mimeOf(file: File): string {
  if (isResourceMime(file.type)) return file.type;
  const dot = file.name.lastIndexOf('.');
  const ext = dot >= 0 ? file.name.slice(dot).toLowerCase() : '';
  return EXT_MIME[ext] ?? file.type;
}

function baseName(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  return dot > 0 ? fileName.slice(0, dot) : fileName;
}

export function ResourcesPage({ tenantId }: { tenantId: string }) {
  const [sites, setSites] = useState<SiteContext[]>([]);
  const [siteId, setSiteId] = useState('');
  const [items, setItems] = useState<LeadResource[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState<LoadPhase>('loading');
  const [renaming, setRenaming] = useState<{ id: string; value: string } | null>(null);
  /**
   * 本批上传的每一份文件。
   *
   * 一个总进度条是看不出"5 份里的第 3 份卡住了"的，而客户经常一次拖进整本目录，
   * 各份大小差得很远（一个 200KB 的证书 + 一个 40MB 的目录）。所以逐份给条。
   */
  const [uploads, setUploads] = useState<UploadRow[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const { confirm, confirmDialog } = useConfirm();
  const uploading = isActive(uploads);
  const uploadPercent = overallPercent(uploads);

  useEffect(() => {
    let cancelled = false;
    fetchSites(tenantId)
      .then((list) => {
        if (cancelled) return;
        setSites(list);
        setSiteId((prev) => (list.some((s) => s.site.id === prev) ? prev : (list[0]?.site.id ?? '')));
        // 一条站点都没有：不是"还在加载"，是真没东西可查，骨架不能永远转
        if (list.length === 0) setPhase('ready');
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message);
        setPhase('error');
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const reload = useCallback(async () => {
    if (!siteId) return;
    setBusy(true);
    try {
      const data = await fetchResources(tenantId, siteId);
      setItems(data.items);
      setPhase('ready');
    } catch (err) {
      setError((err as Error).message);
      setPhase('error');
    } finally {
      setBusy(false);
    }
  }, [tenantId, siteId]);

  useEffect(() => {
    if (!siteId) return;
    setPhase('loading');
    void reload();
  }, [siteId, reload]);

  const patchRow = useCallback((index: number, next: Partial<UploadRow>) => {
    setUploads((prev) => prev.map((row, i) => (i === index ? { ...row, ...next } : row)));
  }, []);

  const uploadOne = async (file: File, index: number): Promise<void> => {
    const mime = mimeOf(file);
    if (!isResourceMime(mime)) throw new Error(`不是支持的类型（PDF / ZIP / DOCX / XLSX / PPTX）`);
    if (file.size > MAX_RESOURCE_BYTES) {
      throw new Error(`超过 ${formatFileSize(MAX_RESOURCE_BYTES)}，请先压缩`);
    }
    if (file.size === 0) throw new Error('是空文件');

    const ticket = await presignResource(tenantId, { siteId, mime, bytes: file.size });
    patchRow(index, { status: 'uploading', total: file.size });

    let lastEmit = 0;
    await putToStorage(ticket, file, (sent, total) => {
      // 进度事件每几毫秒就来一次，40MB 的文件能有上千次回调；每次 setState 都会
      // 重渲染整张表格。按 120ms 节流，肉眼看是连贯的，也不会把主线程占满。
      const now = performance.now();
      if (now - lastEmit < 120) return;
      lastEmit = now;
      patchRow(index, { sent, total });
    });

    // 字节传完了不等于上架了：服务端要用 HeadObject 核对真实大小与类型
    patchRow(index, { status: 'saving', sent: file.size });
    await createResource(tenantId, {
      siteId,
      key: ticket.key,
      name: baseName(file.name).slice(0, 120) || '未命名资料',
      fileName: file.name,
      bytes: file.size,
      mime,
    });
    patchRow(index, { status: 'done', sent: file.size });
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const chosen = Array.from(files).slice(0, 5);
    setBusy(true);
    setError(null);
    setNotice(null);
    setUploads(
      chosen.map((file, index) => ({
        key: `${index}-${file.name}`,
        name: file.name,
        size: file.size,
        total: file.size,
        sent: 0,
        status: 'queued' as const,
      })),
    );
    let done = 0;

    for (const [index, file] of chosen.entries()) {
      try {
        await uploadOne(file, index);
        done += 1;
      } catch (err) {
        // 失败原因写在那一行上，不再重复抛到页面顶部的红字里：客户要的是
        // 「哪一份没上去、为什么」，一条总错误把这份对应关系抹平了
        const message = err instanceof ApiError ? err.message : (err as Error).message;
        patchRow(index, { status: 'failed', error: message });
      }
    }

    setBusy(false);
    if (inputRef.current) inputRef.current.value = '';
    await reload();
    // 成功的行留着没用（下面的列表里已经有了），失败的留下让人看清原因
    setUploads((prev) => prev.filter((row) => row.status === 'failed'));
    if (done > 0) setNotice(`已上传 ${done} 份资料。回到「页面内容」把下载区块指过来即可。`);
  };

  const toggle = async (item: LeadResource) => {
    setBusy(true);
    try {
      await updateResource(tenantId, item.id, { enabled: !item.enabled });
      await reload();
      setNotice(item.enabled ? '已下架：前台会显示「资料已下架」' : '已重新上架');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const rename = async () => {
    if (!renaming) return;
    const value = renaming.value.trim();
    if (!value) {
      setError('名字不能为空');
      return;
    }
    setBusy(true);
    try {
      await updateResource(tenantId, renaming.id, { name: value });
      setRenaming(null);
      await reload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (item: LeadResource) => {
    const ok = await confirm({
      title: `移除「${item.name}」？`,
      message: '文件会被删除，正在用它的下载区块会显示「资料已下架」。已经收到的询盘不受影响。',
      confirmText: '移除',
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await deleteResource(tenantId, item.id);
      await reload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    // 不再写卡片标题与说明：顶栏已经写着「资料库」，副标题就在同一行，
    // 卡片里再来一遍"可被下载的文件…"是第三次说同一件事
    <Card
      actions={
        <div className="platform-actions">
          <Button variant="primary" disabled={busy || !siteId} onClick={() => inputRef.current?.click()}>
            {uploading ? `上传中 ${uploadPercent}%` : busy ? '处理中…' : '上传资料'}
          </Button>
          {/* 原生文件框藏起来，由上面的主按钮触发：系统样式的「选择文件」
              与这套控件不是一套，且它自带的文案不受语言控制 */}
          <input
            ref={inputRef}
            style={visuallyHidden}
            type="file"
            accept={ACCEPT}
            multiple
            onChange={(e) => void handleFiles(e.target.files)}
          />
        </div>
      }
    >
      {sites.length > 1 ? (
        <div className="tablebar">
          <div className="picker">
            <span>站点</span>
            <select className="field" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
              {sites.map((s) => (
                <option key={s.site.id} value={s.site.id}>
                  {s.site.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      ) : null}

      {notice ? <p className="notice">{notice}</p> : null}
      {error ? (
        <p className="error" style={{ whiteSpace: 'pre-line' }}>
          {error}
        </p>
      ) : null}

      {uploads.length > 0 ? (
        <div className="uploadlist" aria-busy={uploading}>
          {uploads.map((row) => (
            <div className="uploadlist__row" key={row.key}>
              <div className="uploadlist__line">
                <span className="uploadlist__name" title={row.name}>
                  {row.name}
                </span>
                <span className="uploadlist__meta">
                  {formatFileSize(row.size)} · {rowLabel(row)}
                </span>
              </div>
              <div className="uploadlist__track">
                <div
                  className={`uploadlist__fill is-${row.status}`}
                  style={{ inlineSize: `${rowPercent(row)}%` }}
                />
              </div>
              {row.error ? <span className="uploadlist__error">{row.error}</span> : null}
            </div>
          ))}
          {uploading ? (
            <p className="uploadlist__hint">
              传完还要由服务器核对一次文件，核对通过才会出现在下面的列表里。上传期间请勿关闭页面。
            </p>
          ) : null}
        </div>
      ) : null}

      {phase === 'loading' ? (
        <p className="muted">正在读取…</p>
      ) : items.length === 0 ? (
        <div className="empty">
          <p className="empty__title">还没有资料</p>
          <p className="muted">
            上传一份产品目录，就能在页面上做一个「留邮箱换下载」的区块 ——
            这是外贸站最稳定的获客方式。
          </p>
        </div>
      ) : (
        <div className="tablewrap">
          <table className="table">
            <thead>
              <tr>
                <th>资料名</th>
                <th>文件</th>
                <th>大小</th>
                <th>下载</th>
                <th>状态</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>
                    {renaming?.id === item.id ? (
                      <input
                        className="field"
                        value={renaming.value}
                        autoFocus
                        onChange={(e) => setRenaming({ id: item.id, value: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void rename();
                          if (e.key === 'Escape') setRenaming(null);
                        }}
                      />
                    ) : (
                      <span className="platform-tenant">{item.name}</span>
                    )}
                  </td>
                  <td className="muted">{item.fileName}</td>
                  <td className="muted platform-nowrap">{formatFileSize(item.bytes)}</td>
                  <td className="platform-nowrap">{item.downloadCount}</td>
                  <td>
                    <Badge tone={item.enabled ? 'success' : 'warn'}>
                      {item.enabled ? '可下载' : '已下架'}
                    </Badge>
                  </td>
                  <td className="platform-nowrap">
                    {/*
                      三个动作三种颜色、都带框：改名（灰）不动线上，下架（琥珀）
                      会让买家立刻下不到，移除（红）不可逆。原来前两个是无框的
                      灰文字，扫一行时根本看不出那是按钮。
                    */}
                    <div className="row-actions">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setRenaming({ id: item.id, value: item.name })}
                      >
                        改名
                      </Button>
                      <Button size="sm" variant="warn" disabled={busy} onClick={() => void toggle(item)}>
                        {item.enabled ? '下架' : '上架'}
                      </Button>
                      <Button size="sm" variant="danger" disabled={busy} onClick={() => void remove(item)}>
                        移除
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="muted platform-meta">
        支持 PDF / ZIP / DOCX / XLSX / PPTX，单份不超过 {formatFileSize(MAX_RESOURCE_BYTES)}。
        下载地址 10 分钟内有效，不填邮箱拿不到直链。
      </p>
      {confirmDialog}
    </Card>
  );
}
